<template>
  <div class="teaching-arrange-schedule-grid">
    <div class="search-bar">
      <el-form :inline="true"
               size="mini"
               @submit.native.prevent>
        <el-form-item label="班级">
          <el-input v-model="searchForm.clazz"
                    class="text-search"
                    clearable
                    placeholder="请输入班级"
                    @input="resetSelection">
          </el-input>
        </el-form-item>
        <el-form-item label="课程名称">
          <el-select v-model="searchForm.courses"
                     class="multiple-search course-search"
                     multiple
                     collapse-tags
                     clearable
                     filterable
                     placeholder="请选择课程"
                     @change="resetSelection">
            <el-option v-for="course in courseOptions"
                       :key="course"
                       :label="course"
                       :value="course">
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="授课教师">
          <el-select v-model="searchForm.teachers"
                     class="multiple-search"
                     multiple
                     collapse-tags
                     clearable
                     filterable
                     placeholder="请选择教师"
                     @change="resetSelection">
            <el-option v-for="teacher in teacherOptions"
                       :key="teacher"
                       :label="teacher"
                       :value="teacher">
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="上课日期">
          <el-date-picker v-model="searchForm.lessonDates"
                          class="date-search"
                          type="daterange"
                          unlink-panels
                          clearable
                          range-separator="至"
                          start-placeholder="开始日期（含）"
                          end-placeholder="结束日期（含）"
                          value-format="yyyy-MM-dd"
                          @change="resetSelection">
          </el-date-picker>
        </el-form-item>
        <el-form-item label="上课时段">
          <el-select v-model="searchForm.lessonPeriods"
                     class="multiple-search period-search"
                     multiple
                     collapse-tags
                     clearable
                     placeholder="请选择时段"
                     @change="resetSelection">
            <el-option v-for="period in periodOptions"
                       :key="period"
                       :label="period"
                       :value="period">
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button :disabled="!hasSearchCondition"
                     @click="resetSearch">清除搜索条件</el-button>
        </el-form-item>
      </el-form>
    </div>
    <div class="grid-table">
      <DataGrid ref="grid"
                size="mini"
                border="full"
                height="auto"
                highlight-hover-column
                highlight-current-column
                show-header-overflow="title"
                resizable
                :loading="loading"
                :data="getArranges"
                :sort-config="{sortMethod: dataSortMethod}"
                :span-method="rowMergeMethod"
                :cell-class-name="cellClassName"
                @cell-click="onCellClick"
                @checkbox-change="onCheckboxChange"
                @checkbox-all="onCheckboxChange"
                @sort-change="onSortChange">
      <DataGridIdColumn selection-mode></DataGridIdColumn>
      <DataGridColumn title="所属教学点"
                      :field="`${groupKey}.site`"
                      width="120"
                      align="center"
                      auto-merge></DataGridColumn>
      <DataGridColumn title="班级"
                      :field="`${groupKey}.clazz`"
                      width="100"
                      auto-merge>
      </DataGridColumn>
      <DataGridColumn title="课程名称"
                      :field="`${groupKey}.course`"
                      sortable
                      width="160">
      </DataGridColumn>
      <DataGridColumn title="上课人数"
                      :field="`${groupKey}.lessonCount`"
                      sortable
                      width="100"
                      align="center">
        <template #default="{row, column}">
          <LessonCount :lesson-count="getValueByPath(row, column.property)"
                       :supply-count="getValueByPath(row, `${groupKey}.supplyCount`)">
          </LessonCount>
        </template>
      </DataGridColumn>
      <DataGridColumn title="发布状态"
                      :field="`${groupKey}.isPublish`"
                      sortable
                      width="100"
                      align="center">
        <template #default="{row, column}">
          <el-switch :value="getValueByPath(row, column.property)"
                     @change="val => changePublish(row, val)"></el-switch>
        </template>
      </DataGridColumn>
      <DataGridColumn title="上课日期"
                      field="lessonDate"
                      sortable
                      width="100"
                      align="center">
      </DataGridColumn>
      <DataGridColumn title="上课时段"
                      field="lessonPeriod"
                      width="120">
      </DataGridColumn>
      <DataGridColumn title="授课教师"
                      field="lessonTeacher"
                      width="160">
      </DataGridColumn>
      <DataGridColumn title="授课地点"
                      field="lessonPlace"
                      width="160">
        <template #default="{row}">
          <div style="display: flex; align-items: center;">
            <span>{{row.lessonPlace}}</span>
            <el-button v-if="row.tencentMapLocation"
                       size="mini"
                       type="text"
                       style="margin-left: 8px; padding: 2px 4px; color: #409EFF;"
                       title="查看地图位置"
                       @click="showMap(row)">
              <i class="el-icon-location-outline"></i>
            </el-button>
          </div>
        </template>
      </DataGridColumn>
      <DataGridColumn title="平时成绩比例"
                      :field="`${groupKey}.usualScoreScale`"
                      sortable
                      width="120"
                      align="right">
        <template #default="{row, column}">
          <span>{{getValueByPath(row, column.property)}}%</span>
        </template>
      </DataGridColumn>
      <DataGridColumn title="考试成绩比例"
                      :field="`${groupKey}.examScoreScale`"
                      sortable
                      width="120"
                      align="right">
        <template #default="{row, column}">
          <span>{{getValueByPath(row, column.property)}}%</span>
        </template>
      </DataGridColumn>
      <DataGridColumn title="作业成绩比例"
                      :field="`${groupKey}.workScoreScale`"
                      sortable
                      width="120"
                      align="right">
        <template #default="{row, column}">
          <span>{{getValueByPath(row, column.property)}}%</span>
        </template>
      </DataGridColumn>
      <DataGridColumn title="实践报告比例"
                      :field="`${groupKey}.reportScoreScale`"
                      sortable
                      width="120"
                      align="right">
        <template #default="{row, column}">
          <span>{{getValueByPath(row, column.property)}}%</span>
        </template>
      </DataGridColumn>
      <DataGridColumn title="案例分析比例"
                      :field="`${groupKey}.caseScoreScale`"
                      sortable
                      width="120"
                      align="right">
        <template #default="{row, column}">
          <span>{{getValueByPath(row, column.property)}}%</span>
        </template>
      </DataGridColumn>
      <DataGridColumn title="综合成绩"
                      :field="`${groupKey}.finalScoreGrade`"
                      sortable
                      width="100"
                      align="center"></DataGridColumn>
      <DataGridColumn title="课程类别"
                      :field="`${groupKey}.courseType`"
                      sortable
                      width="100"
                      align="center"></DataGridColumn>
      <DataGridColumn title="学时"
                      :field="`${groupKey}.creditHour`"
                      sortable
                      width="80"
                      align="center"></DataGridColumn>
      <DataGridColumn title="学分"
                      :field="`${groupKey}.credit`"
                      sortable
                      width="80"
                      align="center"></DataGridColumn>
      <DataGridColumn title="备注"
                      field="remark"
                      min-width="200">
      </DataGridColumn>
      <DataGridColumn title="操作"
                      fixed="right"
                      width="120"
                      align="center">
        <template #default="{row}">
          <el-button size="mini"
                     type="text"
                     @click="handleEdit(row)">编辑</el-button>
          <el-divider direction="vertical"></el-divider>
          <el-button size="mini"
                     type="text"
                     @click="handleDelete(row)">删除</el-button>
        </template>
      </DataGridColumn>
      <template #empty>
        <span>未查询到任何数据</span>
      </template>
      </DataGrid>
    </div>
    <MapModal ref="mapModal"
              :map-location="currentMapLocation"
              :location="currentLocation"
              :map-key="mapConfig.mapKey"
              :circle-radius="mapConfig.maxSignDistance"></MapModal>
  </div>
</template>

<script>
import DataGrid from '../../../../../../../elements/DataGrid'
import DataGridColumn from '../../../../../../../elements/DataGridColumn'
import DataGridIdColumn from '../../../../../../../elements/grid/DataGridIdColumn'
import LessonCount from '../../../../../../../widgets/biz/teaching/LessonCount'
import MapModal from '../../../../../../../widgets/tencent/MapModal'
import objectUtils from '../../../../../../../../util/objectUtils'
import { getValueByPath } from 'element-ui/lib/utils/util'
export default {
  name: 'TeachingArrangeScheduleGrid',
  components: {
    DataGrid,
    DataGridColumn,
    DataGridIdColumn,
    LessonCount,
    MapModal
  },
  props: {
    detailInfo: {
      type: Object,
      required: true
    },
    extraParams: Object,
    mapConfig: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      loading: false,
      schedules: null,
      details: null,
      groupKey: 'group',
      selection: null,
      rowSelection: [],
      currentMapLocation: '',
      currentLocation: '',
      searchForm: {
        clazz: '',
        courses: [],
        teachers: [],
        lessonDates: [],
        lessonPeriods: []
      }
    }
  },
  computed: {
    getSchedules () {
      let schedules = this.schedules
      if (schedules) {
        let scheduleMap = {}
        if (schedules) {
          schedules.forEach(schedule => {
            schedule.children = []
            scheduleMap[schedule.id] = schedule
          })
        }
        let details = this.details
        if (details) {
          details.forEach(detail => {
            let schedule = scheduleMap[detail.scheduleId]
            if (schedule) {
              schedule.children.push(detail)
            }
          })
        }
      }
      return schedules
    },
    getArranges () {
      let schedules = this.getSchedules
      if (schedules) {
        const groupKey = this.groupKey
        let arranges = []
        schedules.forEach(schedule => {
          let children = schedule.children
          if (children && children.length) {
            children.forEach(detail => {
              detail[groupKey] = schedule
              arranges.push(detail)
            })
          }
        })
        return arranges.filter(row => this.matchesSearchConditions(row))
      }
      return []
    },
    courseOptions () {
      let courses = []
      const schedules = this.schedules || []
      schedules.forEach(schedule => {
        if (schedule.course && !courses.includes(schedule.course)) {
          courses.push(schedule.course)
        }
      })
      return courses
    },
    periodOptions () {
      return (this.detailInfo.periods || [])
        .map(v => v.name)
    },
    teacherOptions () {
      return (this.detailInfo.teachers || [])
        .map(v => v.name)
        .filter((name, index, teachers) => name && teachers.indexOf(name) === index)
    },
    hasSearchCondition () {
      const searchForm = this.searchForm
      return !!searchForm.clazz.trim() ||
        searchForm.courses.length > 0 ||
        searchForm.teachers.length > 0 ||
        (searchForm.lessonDates && searchForm.lessonDates.length > 0) ||
        searchForm.lessonPeriods.length > 0
    }
  },
  methods: {
    queryArranges () {
      if (this.loading) {
        return
      }
      this.resetSelection()
      this.loading = true
      this.$ajax({
        url: '/workspace/manager/teaching/schedule/arrange/query',
        method: 'post',
        data: this.$qs.stringify(this.extraParams)
      }).then(response => {
        if (response.data) {
          switch (response.data['responseCode']) {
            case 'SUCCESS': {
              this.schedules = response.data['schedules'] || []
              this.details = response.data['details'] || []
              break
            }
            default: {
              this.$message.warning(response.data['message'])
              break
            }
          }
        }
      }, () => {
        this.$message.error('请求发送失败，请稍后重试')
      }).finally(() => {
        this.loading = false
      })
    },
    getValueByPath,
    matchesSearchConditions (row) {
      const searchForm = this.searchForm
      const schedule = row[this.groupKey] || {}
      const clazz = searchForm.clazz.trim().toLowerCase()
      if (clazz && !String(schedule.clazz || '').toLowerCase().includes(clazz)) {
        return false
      }
      if (searchForm.courses.length && !searchForm.courses.includes(schedule.course)) {
        return false
      }
      if (searchForm.teachers.length && !searchForm.teachers.includes(row.lessonTeacher)) {
        return false
      }
      if (searchForm.lessonPeriods.length) {
        const lessonPeriod = String(row.lessonPeriod || '')
        const matchesPeriod = searchForm.lessonPeriods.some(period => lessonPeriod.includes(period))
        if (!matchesPeriod) {
          return false
        }
      }
      if (searchForm.lessonDates && searchForm.lessonDates.length === 2) {
        const lessonDate = row.lessonDate
        if (!lessonDate || lessonDate < searchForm.lessonDates[0] || lessonDate > searchForm.lessonDates[1]) {
          return false
        }
      }
      return true
    },
    resetSelection () {
      this.selection = null
      this.rowSelection = []
      const grid = this.$refs.grid
      if (grid) {
        grid.clearCheckboxRow()
      }
      this.$emit('selection-change', [], [])
    },
    resetSearch () {
      this.searchForm.clazz = ''
      this.searchForm.courses = []
      this.searchForm.teachers = []
      this.searchForm.lessonDates = []
      this.searchForm.lessonPeriods = []
      this.resetSelection()
    },
    rowMergeMethod ({row, $rowIndex, column, data}) {
      const property = column.property
      const checkKey = this.groupKey
      if (property && property.startsWith(checkKey + '.')) {
        const cellValue = getValueByPath(row, property)
        const autoMerge = column.own && column.own.$attrs.hasOwnProperty('auto-merge')
        const prevRow = data[$rowIndex - 1]
        if (prevRow && (row[checkKey] === prevRow[checkKey] || (autoMerge && objectUtils.hasValue(cellValue) && getValueByPath(prevRow, property) === cellValue))) {
          return { rowspan: 0, colspan: 0 }
        } else {
          let nextRow = data[$rowIndex + 1]
          let countRowspan = 1
          while (nextRow) {
            if (row[checkKey] === nextRow[checkKey] || (autoMerge && objectUtils.hasValue(cellValue) && getValueByPath(nextRow, property) === cellValue)) {
              nextRow = data[++countRowspan + $rowIndex]
            } else {
              break
            }
          }
          if (countRowspan > 1) {
            return { rowspan: countRowspan, colspan: 1 }
          }
        }
      }
    },
    dataSortMethod ({column, data, order}) {
      let result = data
      if (column.property) {
        if (order) {
          result = data.sort((d1, d2) => {
            // 同一组才可以排序
            if (d1[this.groupKey] === d2[this.groupKey]) {
              let v1 = getValueByPath(d1, column.property)
              let v2 = getValueByPath(d2, column.property)
              if (objectUtils.hasValue(v1)) {
                if (objectUtils.hasValue(v2)) {
                  return v1 > v2 ? 1 : -1
                } else {
                  return 1
                }
              } else {
                return -1
              }
            } else {
              return -1
            }
          })
          if (order === 'desc') {
            result.reverse()
          }
        }
      }
      return result
    },
    cellClassName (cell) {
      const selection = this.selection
      if (selection) {
        if (cell.$rowIndex >= selection.start && cell.$rowIndex < selection.end) {
          let merge = this.rowMergeMethod(cell)
          let end = cell.$rowIndex + (merge ? merge.rowspan : 1)
          if (end <= selection.end) {
            return 'highlight'
          }
        }
      }
    },
    onCellClick (current) {
      let merge = this.rowMergeMethod(current)
      this.selection = {
        start: current.$rowIndex,
        end: current.$rowIndex + (merge ? merge.rowspan : 1)
      }
      this.$nextTick(() => {
        this.$refs.grid.clearCurrentColumn()
      })
    },
    onCheckboxChange ({selection}) {
      this.rowSelection = selection
      const scheduleMap = new Map()
      selection.forEach(row => {
        const schedule = row[this.groupKey]
        if (schedule && schedule.id) {
          scheduleMap.set(schedule.id, schedule)
        }
      })
      this.$emit('selection-change', selection.slice(), Array.from(scheduleMap.values()))
    },
    onSortChange () {
      this.resetSelection()
    },
    handleEdit (row) {
      this.$emit('on-edit', row)
    },
    handleDelete (row) {
      let schedule = row[this.groupKey]
      let children, isPublish, isSupply
      if (schedule) {
        children = schedule.children
        isPublish = schedule.isPublish
        isSupply = !!schedule.supplyCount
      }
      let confirmText, confirmType
      if (children && children.length <= 1) {
        if (isPublish) {
          confirmText = `此操作将取消该课程${isSupply ? '（补修学生也将被删除）' : ''}，是否继续？`
        } else {
          confirmText = '此操作将取消该课程，是否继续？'
        }
        confirmType = 'error'
      } else {
        confirmText = '此操作将删除该课程安排，是否继续？'
        confirmType = 'warning'
      }
      this.$confirm(confirmText, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: confirmType
      }).then(() => {
        this.$emit('on-delete', row)
      }).catch(() => {})
    },
    changePublish (row, val) {
      let schedule = row[this.groupKey]
      let confirmText, confirmType
      if (val) {
        confirmText = '此操作将发布该课程，是否继续？'
        confirmType = 'warning'
      } else {
        let isSupply
        if (schedule) {
          isSupply = !!schedule.supplyCount
        }
        confirmText = `此操作将下架该课程${isSupply ? '（补修学生也将被删除）' : ''}，是否继续？`
        confirmType = 'error'
      }
      this.$confirm(confirmText, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: confirmType
      }).then(() => {
        this.$emit('on-change', schedule, val)
      }).catch(() => {})
    },
    showMap (row) {
      // 只有tencentMapLocation存在时才显示地图
      if (!row.tencentMapLocation || !row.tencentMapLocation.trim()) {
        this.$message.warning('该课程没有地图位置信息')
        return
      }

      // 清空之前的数据
      this.currentMapLocation = ''
      this.currentLocation = ''

      // 使用tencentMapLocation（坐标）
      this.currentMapLocation = row.tencentMapLocation.trim()
      // 如果有地址名称，也传递过去作为位置标识
      if (row.lessonPlace && row.lessonPlace.trim()) {
        this.currentLocation = row.lessonPlace.trim()
      }

      this.$nextTick(() => {
        this.$refs.mapModal.show()
      })
    }
  },
  mounted () {
    this.queryArranges()
  }
}
</script>

<style lang="scss">
  .teaching-arrange-schedule-grid {
    height: 100%;
    display: flex;
    flex-direction: column;
    & > .search-bar {
      flex: none;
      padding: 10px 12px 2px;
      border-bottom: 1px solid #ebeef5;
      background-color: #fff;
      .el-form {
        display: flex;
        align-items: flex-start;
        flex-wrap: wrap;
        .el-form-item {
          margin-right: 12px;
          margin-bottom: 8px;
          .text-search,
          .multiple-search {
            width: 180px;
          }
          .period-search {
            width: 150px;
          }
          .course-search {
            width: 280px;
            .el-select__tags {
              overflow: hidden;
              white-space: nowrap;
              & > span {
                display: flex;
                align-items: center;
                flex-wrap: nowrap;
                min-width: 0;
                overflow: hidden;
              }
              .el-tag {
                flex: none;
                max-width: 220px;
                .el-select__tags-text {
                  display: inline-block;
                  max-width: 175px;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  white-space: nowrap;
                  vertical-align: bottom;
                }
              }
            }
          }
          .date-search {
            width: 300px;
          }
        }
      }
    }
    & > .grid-table {
      flex: auto;
      min-height: 0;
      position: relative;
      & > .vxe-table {
        height: 100%!important;
        border: none!important;
        &:before {
          display: none;
        }
        .vxe-header--column {
          &.is--filter {
            .vxe-cell {
              .vxe-cell--filter {
                padding-left: 5px;
                i {
                  font-size: 12px!important;
                }
              }
            }
          }
        }
        .vxe-body--row {
          .vxe-body--column {
            .vxe-cell {
              .el-switch {
                transform: scale(.72);
                vertical-align: -5px;
              }
            }
            &.highlight {
              background-color: #fff3e0;
            }
          }
        }
        .vxe-table--fixed-left-wrapper {
          box-shadow: none;
          &.scrolling--middle {
            border-right: 1px solid rgba(0,0,0,.05);
          }
        }
        .vxe-table--fixed-right-wrapper {
          &.scrolling--middle {
            border-left: 1px solid rgba(0,0,0,.05);
            box-shadow: -2px 0 2px rgba(0,0,0,.05);
          }
        }
        .vxe-table--border-line {
          display: none;
        }
        .vxe-table--loading {
          background-color: rgba(255,255,255,.5);
        }
        .vxe-table--filter-wrapper {
          border: 1px solid #ebeef5;
          border-radius: 4px;
          box-shadow: 0 6px 6px rgba(0,0,0,.1);
          .vxe-table--filter-footer {
            border-top-color: #ebeef5;
          }
        }
      }
    }
  }
</style>
